export type Base64 = ArrayBuffer | string;

/**
 * @enum
 */
export enum ImageType {
  original = 'original',
  augmented = 'augmented',
}

export enum CameraFaceMode {
  front = 'front',
  back = 'back',
}

export enum WasmVersion {
  face = 'face',
  face_mask = 'face_mask',
  voice = 'voice',
}

export enum FacingMode {
  face = 'face',
  environment = 'environment',
}

/**
 * @enum
 */
export enum FaceStatuses {
  VALID_FACE = 'VALID_FACE',
  INVALID_FACE = 'INVALID_FACE',
  ENROLLING = 'ENROLLING',
  PREDICTING = 'PREDICTING',
  WASM_RESPONSE = 'WASM_RESPONSE',
  ERROR_ENROLL = 'ERROR_ENROLL',
  ERROR_PREDICT = 'ERROR_PREDICT',
  REDIRECT = 'REDIRECT',
}

export enum VoiceStatuses {
  SET_PHRASES = 'SET_PHRASES',
  SET_PROGRESS = 'SET_PROGRESS',
  START_RECORDING = 'START_RECORDING',
  RECORDING_DONE = 'RECORDING_DONE',
  PREDICTION_DONE = 'PREDICTION_DONE',
}

export enum DocType {
  PHOTO_ID_BACK = 'PHOTO_ID_BACK',
  PHOTO_ID_FRONT = 'PHOTO_ID_FRONT',
}

export enum LOGTYPE {
  ERROR = 'ERROR',
  LOG = 'LOG',
  WARN = 'WARN',
}

export enum DLType {
  FRONTDLORIGINAL = 'frontDLoriginal',
  FRONTDLHEADSHOT = 'frontDLheadshot',
  FRONTDLCROPPED = 'frontDLcropped',
  BACKDLORIGINAL = 'backDLoriginal',
  BACKDLBARCODE = 'backDLbarcode',
  BARCODEJSON = 'barcodeJson'
}

/**
 * NPM: uploadDl({ id, type, image })
	id (string): GAN token //Required
	type: “frontDLoriginal”, “frontDLheadshot”, “frontDLcropped”, “backDLoriginal”, “backDLbarcode”
	image: string // Base64 //Require
 */

export enum VerifyType {
  PHONE = 'phone',
  EMAIL = 'email',
}

declare global {
  interface Window {
    opera: any;
    mozIndexedDB: any;
    webkitIndexedDB: any;
    msIndexedDB: any;
    shimIndexedDB: any;
  }
}

export type WorkerFunctions = {
  isLoad: (simd: boolean, url: string, key: string, module: WasmVersion, debug_type: string, cacheConfig: boolean) => Promise<string>;

  loadAntispoof: (simd:boolean) => Promise<boolean>;

  deleteUUID: (uuid: string, cb: any) => void;

  FHE_enroll: (
    originalImages: Array<ImageData>,
    simd: boolean,
    action: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<{ result: number; href: ImageData[] }>;

  FHE_enrollOnefa: (
    originalImages: Array<ImageData>,
    simd: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<{ result: number; href: ImageData[] }>;

  FHE_predictOnefa: (
    originalImages: Array<ImageData>,
    simd: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<{ result: number; href: ImageData[] }>;

  scanDocument: (
    imageInput: ImageData,
    simd: boolean,
    cb: any,
    doPredict:boolean,
    config: string,
    debug_type?: string,
    scanDocumentData?: any,
  ) => Promise<{ result: number;
    croppedDocument: Uint8ClampedArray | null;
    croppedMugshot: Uint8ClampedArray | null;
  }>;

  isValidBarCode: (
    imageInput: ImageData,
    simd: boolean,
    cb: any,
    config: string,
    debug_type: string,
  ) => Promise<{
    result: number;
    croppedDocument: Uint8ClampedArray | null;
    croppedBarcode: Uint8ClampedArray | null;
  }>;

  isValidFrontDocument: (
    imagePtr: PointerEvent,
    width: number,
    height: number,
    simd: boolean,
    action: boolean,
    debug_type: string,
    cb: any,
  ) => Promise<number>;

  isValidInternal: (
    data: Uint8ClampedArray,
    width: number,
    height: number,
    simd: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<{ result: number; }>;

  prividAgePredict: (
    data: Uint8ClampedArray,
    width: number,
    height: number,
    simd: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<{ result: number; ageFactor?: number }>;

  voicePredict: (buffer: ArrayBuffer) => Promise<number>;

  isValidVoice: (
    data: ArrayBuffer,
    action: boolean,
    params: { [key: string]: number },
    recordDuration: number,
    simd: boolean,
    debug_type: string,
  ) => Promise<{ result: number; voiceData: Uint8ClampedArray }>;

  prividFaceISO: (
    imageInput: ImageData,
    simd: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<
  {
    result: number;
    imageOutput?:  Uint8ClampedArray | null
  }>

  prividFaceCompareLocal: (
    imageInputA: ImageData,
    imageInputB: ImageData,
    simd: boolean,
    debug_type: string,
    cb: any,
    config?: string,
  ) => Promise<
  {
    result: number;
  }>

  antispoofCheck: (
    data: Uint8ClampedArray,
    width: number,
    height: number,
  ) => Promise<{ livenessCheck: number; }>;
};

export interface verificationSessionPayload {
  successUrl?: string;
  failureUrl?: string;
  type?: string;
  productGroupId?: string;
}